// Apolo IDE — gitContext (core PURO, sprint composer-anchor E1).
//
// Fonte única de verdade do ALVO do composer. Recebe dados JÁ LIDOS (pelos
// hooks/IPC) e devolve um veredito determinístico: qual repo/branch/dir o
// agente vai operar + a SAÚDE dessa relação. Não importa React, nem window,
// nem a store — é testável só com vitest (Dependency Rule, ver 01-DOCTRINE).
//
// A pref salva (`selectedRepo` = ui.github_repo) é só um INPUT. Quando ela
// diverge do origin real do diretório, isso vira `health:'conflict'` — nunca
// é seguido em silêncio. Foi a bifurcação que fez a home reabrir o glyco.

/** @typedef {{ name?: string, full_name?: string, url?: string, clone_url?: string, default_branch?: string, private?: boolean } | null} SelectedRepo */
/** @typedef {{ ok?: boolean, url?: string, owner?: string, repo?: string } | null} LiveOrigin */
/** @typedef {'synced'|'unconfirmed'|'conflict'} Health */
/**
 * @typedef {Object} GitContext
 * @property {string|null} owner
 * @property {string|null} repo
 * @property {string|null} slug          owner/repo autoritativo
 * @property {string|null} branch
 * @property {string|null} dir
 * @property {string|null} origin         slug do origin VIVO do dir (ou null)
 * @property {string|null} selectedSlug   slug da pref (intenção), p/ o banner
 * @property {Health} health
 * @property {string} reason              legível, p/ aria/banner
 * @property {boolean} protectedBranch
 */

const PROTECTED_BRANCH_RE = /^(main|master)$/i;
const RELEASE_BRANCH_RE = /^release\//i;

function norm(s) {
  return typeof s === 'string' ? s.trim().toLowerCase() : '';
}

function liveSlugOf(liveOrigin) {
  if (liveOrigin && liveOrigin.owner && liveOrigin.repo) {
    return `${liveOrigin.owner}/${liveOrigin.repo}`;
  }
  return null;
}

function splitSlug(slug) {
  if (!slug || typeof slug !== 'string' || !slug.includes('/')) {
    return { owner: null, repo: slug || null };
  }
  const i = slug.indexOf('/');
  return { owner: slug.slice(0, i), repo: slug.slice(i + 1) };
}

export function isProtectedBranch(branch) {
  if (!branch || typeof branch !== 'string') return false;
  return PROTECTED_BRANCH_RE.test(branch) || RELEASE_BRANCH_RE.test(branch);
}

/**
 * Resolve o contexto git do composer a partir de dados já lidos.
 * @param {{ selectedRepo?: SelectedRepo, detected?: ({ ok?: boolean, path?: string }|null), liveOrigin?: LiveOrigin, liveBranch?: (string|null), dir?: (string|null) }} input
 * @returns {GitContext}
 */
export function resolveGitContext(input = {}) {
  const { selectedRepo = null, liveOrigin = null, liveBranch = null } = input;
  const dir = input.dir ?? input.detected?.path ?? null;

  const liveSlug = liveSlugOf(liveOrigin);
  const selectedSlug =
    selectedRepo && selectedRepo.full_name ? selectedRepo.full_name : null;

  const hasSelected = !!selectedSlug;

  /** @type {Health} */
  let health;
  let reason;
  if (hasSelected && liveSlug && norm(selectedSlug) !== norm(liveSlug)) {
    health = 'conflict';
    reason = `O repo selecionado (${selectedSlug}) não bate com o origin da pasta (${liveSlug}).`;
  } else if (hasSelected && liveSlug) {
    health = 'synced';
    reason = `Repo selecionado confere com o origin da pasta (${liveSlug}).`;
  } else if (hasSelected && !liveSlug) {
    health = 'unconfirmed';
    reason = `Repo selecionado (${selectedSlug}) ainda não confirmado contra o git da pasta.`;
  } else if (liveSlug) {
    health = 'synced';
    reason = `Operando no git da pasta (${liveSlug}).`;
  } else {
    // Sem seleção e sem origin vivo: pasta local sem remote (ou sem git). Não há
    // o que conflitar — synced, mas honesto sobre não ter origin.
    health = 'synced';
    reason = dir ? 'Pasta local sem origin remoto.' : 'Sem alvo configurado.';
  }

  const slug = liveSlug ?? selectedSlug ?? null;
  const { owner, repo } = splitSlug(slug);
  const branch = liveBranch ?? selectedRepo?.default_branch ?? null;

  return {
    owner,
    repo,
    slug,
    branch,
    dir,
    origin: liveSlug,
    selectedSlug,
    health,
    reason,
    protectedBranch: isProtectedBranch(branch),
  };
}
